import { Fragment, useEffect, useMemo, useState } from "react";
import Typography from "@mui/material/Typography";
import { Box, Grid, InputLabel, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import * as yup from "yup";
import { useFormik } from "formik";
import { useLocation } from "react-router-dom";
import {
    headerStyle,
    formLabelStyle,
    signInFormContainer,
    formTextInputStyle,
    formSubmitButtonStyle,
    rememberMeContainerStyle,
    rememberMeLabelStyle,
} from "./Form.styles";
import useAuth from "src/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { defaultRoute } from "src/context/CurrentRouteContext";
import useMessage from "src/hooks/useMessage";
import SpinnerLoader from "src/components/loader/SpinnerLoader";
import PasswordField from "src/components/input/PasswordTextField";
import { getAuthPageContainerStyle } from "src/utils/helperFunctions";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";

function SignInForm() {
    const auth = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { showSuccess, showError } = useMessage();

    // Sign In Page General State

    const initialMessage = location.state?.message;
    const containerStyle = useMemo(() => getAuthPageContainerStyle(window), []);

    const [loginLoading, setLoginLoading] = useState<boolean>(false);
    const [authorized, setAuthorized] = useState<boolean>(true);
    const [isRememberMe, setIsRememberMe] = useState<boolean>(false);

    useEffect(() => {
        let timeout: NodeJS.Timeout;
        window.history.replaceState({}, document.title);
        if (initialMessage) {
            timeout = setTimeout(() => {
                showSuccess(initialMessage);
            }, 500);
        }
        return () => clearTimeout(timeout);
    }, []);

    useEffect(() => {
        if (auth.isLogged || auth.user) {
            const path = localStorage.getItem("currentRoute") || defaultRoute;
            setAuthorized(true);
            navigate(path);
        } else {
            setAuthorized(false);
        }
    }, [auth]);

    const validationSchema = yup.object({
        username: yup.string().required("Username is required"),
        password: yup.string().required("Password is required"),
    });

    const formik = useFormik({
        initialValues: {
            username: "",
            password: "",
        },
        validationSchema,
        async onSubmit(values) {
            setLoginLoading(true);
            try {
                await auth.login(
                    values.username,
                    values.password,
                    isRememberMe,
                );
                navigate("/dashboard/preBids");
            } catch (error: any) {
                const errorMessage =
                    error?.response?.data?.message || error.message;
                const errorCode = error?.response?.status;

                if (errorMessage === "Incorrect username or password") {
                    showError("Username or password is incorrect");
                } else if (errorCode === 500) {
                    showError("Internal server error");
                } else if (errorMessage.includes("disabled")) {
                    showError(
                        `Your account has been disabled. Please contact ${process.env.REACT_APP_DEFAULT_USER_EMAIL}`,
                    );
                } else if (errorMessage.includes("notUser")) {
                    showError("User not authorized");
                } else {
                    showError("User login failed");
                }
            } finally {
                setLoginLoading(false);
            }
        },
    });

    return !authorized ? (
        <Fragment>
            <Grid
                zIndex={0}
                container
                sx={containerStyle}
                alignContent={"left"}
            >
                <Grid item xs={12} md={6} lg={4.5} sx={signInFormContainer}>
                    <Typography sx={headerStyle}>SIGN IN</Typography>
                    <form onSubmit={formik.handleSubmit} autoComplete="off">
                        <InputLabel htmlFor="username" sx={formLabelStyle}>
                            Username
                        </InputLabel>
                        <TextField
                            fullWidth
                            id="username"
                            name="username"
                            autoComplete="off"
                            value={formik.values.username}
                            sx={formTextInputStyle}
                            onChange={(event) => {
                                const lowerCaseValue =
                                    event.target.value.trim();
                                formik.setFieldValue(
                                    "username",
                                    lowerCaseValue,
                                );
                            }}
                            error={
                                formik.touched.username &&
                                Boolean(formik.errors.username)
                            }
                            helperText={
                                formik.touched.username &&
                                formik.errors.username
                            }
                        />
                        <Box
                            display={"flex"}
                            flexDirection={"row"}
                            alignItems={"center"}
                            justifyContent="space-between"
                        >
                            <InputLabel htmlFor="password" sx={formLabelStyle}>
                                Password
                            </InputLabel>
                            <Typography
                                sx={{ ...formLabelStyle, cursor: "pointer" }}
                                onClick={() => navigate("/forgotPassword")}
                            >
                                {"Forgot password?"}
                            </Typography>
                        </Box>
                        <PasswordField
                            form={formik}
                            sx={formTextInputStyle}
                            formValueName="password"
                        />
                        <Box
                            display={"flex"}
                            flexDirection={"row"}
                            alignItems={"center"}
                            sx={rememberMeContainerStyle}
                            onClick={() =>
                                setIsRememberMe((prevState) => !prevState)
                            }
                        >
                            {isRememberMe ? (
                                <CheckBoxIcon sx={{ color: "#efd016" }} />
                            ) : (
                                <CheckBoxOutlineBlankIcon
                                    sx={{ color: "white" }}
                                />
                            )}
                            <Typography sx={rememberMeLabelStyle}>
                                Remember Me
                            </Typography>
                        </Box>
                        <Button
                            type="submit"
                            sx={formSubmitButtonStyle}
                            disabled={loginLoading}
                            endIcon={
                                <SpinnerLoader
                                    loading={loginLoading}
                                    size={20}
                                    sx={{ marginLeft: "10px" }}
                                />
                            }
                        >
                            Sign In
                        </Button>
                    </form>
                    <Box
                        display={"flex"}
                        flexDirection={"row"}
                        alignItems={"center"}
                        marginTop={"20px"}
                    >
                        <Typography
                            sx={{ ...formLabelStyle, marginRight: "10px" }}
                        >
                            Don&apos;t have an account?
                        </Typography>
                        <Typography
                            sx={{
                                ...formLabelStyle,
                                cursor: "pointer",
                                color: "#EFD016",
                            }}
                            onClick={() => navigate("/register")}
                        >
                            Sign Up
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </Fragment>
    ) : null;
}

export default SignInForm;
